import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxPopupModule, DxTextBoxModule, DxButtonModule, DxLoadIndicatorModule } from 'devextreme-angular';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CrudService } from '../services/crud.service';
import { environment } from 'src/environments/environment';

export interface PriceWatcherOffer {
  retailer: string;
  price: number;
  url: string | null;
  note: string | null;
  sellerType: 'retail' | 'venue';
  verified: boolean;
  verifiedAt: string | null;
  verificationNote: string | null;
}

export interface PriceWatcherResult {
  productName: string;
  category: string | null;
  currency: string;
  offers: PriceWatcherOffer[];
  bestPrice: number;
  typicalPrice: number;
  lowPrice: number;
  lowPriceRetailer: string | null;
  lowPriceOnPromotion: boolean;
  highPrice: number;
  verdict: 'great_deal' | 'good_deal' | 'fair_price' | 'overpriced' | 'no_retail' | null;
  dealScore: number | null;
  summary: string;
  /** True while only the instant BWS prices are in and the AI phase is still researching. */
  partial?: boolean;
}

export interface PriceWatcherLookup {
  id: number;
  query: string;
  postcode: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result: PriceWatcherResult | null;
  error: string | null;
  completed_at: string | null;
}

const VERDICT_LABELS: Record<string, string> = {
  great_deal: 'Great deal',
  good_deal: 'Good deal',
  fair_price: 'Fair price',
  overpriced: 'Overpriced',
  no_retail: 'No retail prices'
};

// Lookups normally finish in ~10s (web search + live verification) — keep the user informed.
const LOADING_MESSAGES = [
  'Searching retailers across the web…',
  'Comparing offers and pack sizes…',
  'Verifying prices against live retailer sites…',
  'Working out the deal verdict…'
];

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 90; // ~3 minutes — generous headroom over the ~10s typical lookup

@Component({
  selector: 'app-price-watcher',
  imports: [CommonModule, DxPopupModule, DxTextBoxModule, DxButtonModule, DxLoadIndicatorModule],
  templateUrl: './price-watcher.component.html',
  styleUrl: './price-watcher.component.css',
  standalone: true
})
export class PriceWatcherComponent implements OnDestroy {
  @Input() visible: boolean = false;
  @Output() closed = new EventEmitter<void>();

  query: string = '';
  loading: boolean = false;
  loadingMessage: string = LOADING_MESSAGES[0];
  lookup: PriceWatcherLookup | null = null;
  errorMessage: string | null = null;

  private apiUrl: string = environment.baseApiUrl;
  private pollSubscription: Subscription | null = null;
  private loadingMessageSubscription: Subscription | null = null;
  private topUpSubscription: Subscription | null = null;

  constructor(private crudService: CrudService) {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onHiding(): void {
    this.stopPolling();
    this.loading = false;
    this.closed.emit();
  }

  onSearch(force: boolean = false): void {
    const query = this.query?.trim();
    if (!query || this.loading) {
      return;
    }
    this.stopPolling();
    this.errorMessage = null;
    this.lookup = null;
    this.loading = true;
    this.startLoadingMessages();

    const body: any = { query };
    if (force) {
      body.force = true;
    }

    this.crudService.insertData(`${this.apiUrl}price-watcher/lookups`, body).subscribe({
      next: (response: any) => {
        const lookup = response?.data as PriceWatcherLookup;
        if (!lookup) {
          this.failWith('The price lookup could not be started. Please try again.');
          return;
        }
        if (lookup.status === 'completed' || lookup.status === 'failed') {
          this.finishWith(lookup);
        } else {
          // May already carry the instant BWS prices as a partial result.
          this.lookup = lookup;
          this.pollLookup(lookup.id);
        }
      },
      error: () => this.failWith('The price lookup could not be started. Please try again.')
    });
  }

  onRefresh(): void {
    this.onSearch(true);
  }

  /** Partial (BWS-only) while the AI phase runs, then the final result. */
  get result(): PriceWatcherResult | null {
    return this.lookup?.result ?? null;
  }

  get isPartial(): boolean {
    return this.loading && this.result?.partial === true;
  }

  get retailOffers(): PriceWatcherOffer[] {
    return this.result?.offers.filter(o => o.sellerType === 'retail') ?? [];
  }

  get venueOffers(): PriceWatcherOffer[] {
    return this.result?.offers.filter(o => o.sellerType === 'venue') ?? [];
  }

  get verdictLabel(): string {
    const verdict = this.result?.verdict;
    return verdict ? (VERDICT_LABELS[verdict] ?? verdict) : '';
  }

  get checkedAgo(): string {
    const completedAt = this.lookup?.completed_at;
    if (!completedAt) {
      return '';
    }
    const minutes = Math.max(0, Math.round((Date.now() - new Date(completedAt).getTime()) / 60000));
    if (minutes < 1) {
      return 'just now';
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    const hours = Math.round(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  verificationTooltip(offer: PriceWatcherOffer): string {
    if (offer.verified) {
      const when = offer.verifiedAt ? ` at ${new Date(offer.verifiedAt).toLocaleString()}` : '';
      return (offer.verificationNote ? `${offer.verificationNote} — ` : '') + `Confirmed against the live retailer page${when}`;
    }
    return offer.verificationNote ?? 'Could not be confirmed against the retailer page';
  }

  private pollLookup(id: number): void {
    let polls = 0;
    this.pollSubscription = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.crudService.newGetData(`${this.apiUrl}price-watcher/lookups/${id}`)))
      .subscribe({
        next: (response: any) => {
          polls++;
          const lookup = response?.data as PriceWatcherLookup;
          if (lookup && (lookup.status === 'completed' || lookup.status === 'failed')) {
            this.finishWith(lookup);
          } else if (polls >= MAX_POLLS) {
            this.failWith('The price lookup is taking too long. Please try again.');
          } else if (lookup) {
            this.lookup = lookup; // keeps any partial result fresh while waiting
          }
        },
        error: () => this.failWith('Lost contact with the price lookup. Please try again.')
      });
  }

  private finishWith(lookup: PriceWatcherLookup): void {
    this.stopPolling();
    this.loading = false;
    this.lookup = lookup;
    if (lookup.status === 'failed') {
      this.errorMessage = lookup.error || 'The price lookup failed. Please try again.';
    } else {
      this.watchForTopUp(lookup.id);
    }
  }

  /**
   * The background top-up may verify more offers (headless page renders)
   * shortly after the lookup completes — quietly re-fetch a few times so
   * Verified badges and corrected prices upgrade in the open popup.
   */
  private watchForTopUp(id: number): void {
    let checks = 0;
    this.topUpSubscription = timer(10000, 15000)
      .pipe(switchMap(() => this.crudService.newGetData(`${this.apiUrl}price-watcher/lookups/${id}`)))
      .subscribe({
        next: (response: any) => {
          checks++;
          const lookup = response?.data as PriceWatcherLookup;
          if (lookup?.status === 'completed') {
            this.lookup = lookup;
          }
          if (checks >= 4) {
            this.topUpSubscription?.unsubscribe();
            this.topUpSubscription = null;
          }
        },
        error: () => {
          this.topUpSubscription?.unsubscribe();
          this.topUpSubscription = null;
        }
      });
  }

  private failWith(message: string): void {
    this.stopPolling();
    this.loading = false;
    this.errorMessage = message;
  }

  private startLoadingMessages(): void {
    let index = 0;
    this.loadingMessage = LOADING_MESSAGES[0];
    this.loadingMessageSubscription = timer(6000, 6000).subscribe(() => {
      index = (index + 1) % LOADING_MESSAGES.length;
      this.loadingMessage = LOADING_MESSAGES[index];
    });
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    this.loadingMessageSubscription?.unsubscribe();
    this.loadingMessageSubscription = null;
    this.topUpSubscription?.unsubscribe();
    this.topUpSubscription = null;
  }
}
